import { useHierarchyArea } from '@plarium/hierarchy-react';
import React, { useCallback, useEffect } from 'react';

import isEmpty from 'lodash/isEmpty';

import { Section, Grid, Alert, Select } from '@gogame/components';
import {
  FormField,
  FormFieldGroup,
  withForm,
  SidePageV2,
  withFormField,
  useChangeMultipleValues,
  omitFormProps,
} from '@gogame/form';
import { useMountedState } from '@gogame/hooks-ts';

import {
  loadUsers,
  loadActiveUsers,
  loadMasterCampaigns,
  loadMasterCampaign,
  loadPlatforms,
  loadBriefs,
  loadCountries,
  loadPromoCodes,
  loadRewardPackages,
  loadLandingPages,
  loadIOSLandingPages,
  loadAndroidLandingPages,
  loadAgencies,
  loadInfuencersChannels,
  loadPriceWithFee,
  loadLocations,
  loadCostModels,
} from 'scenes/Influencers/InfluencersCampaigns/sources/fields';

import AdditionalRewards from './AdditionalRewards';
import { CostModelGroup } from './CostModel';
import GGCampaignLinks, {
  ggCampaignLinkSchema,
  PLATFORM_ID,
  HYBRID_LINK_PARAMETERS,
  IOS_CPP,
  ANDROID_CPP,
} from './GGCampaignLinks';
import { HybridLinkParameters } from './HybridLinkParameters';
import MultiChangeSelect from './MultiChangeSelect';
import {
  NAME,
  OWNER,
  GENERATE_NAME_AUTOMATICALLY,
  MASTER_CAMPAIGN,
  PLATFORMS,
  BRIEF,
  COUNTRY,
  REWARD_PACKAGE,
  ADDITIONAL_REWARDS,
  PROMO_CODE,
  LANDING_PAGE,
  IOS_LANDING_PAGE,
  ANDROID_LANDING_PAGE,
  AGENCY,
  INFLUENCER_CHANNEL,
  COST_MODEL,
  PAYOUT,
  CREATIVE_URL,
  PROMO_LOCATION,
  PROMO_STARTING_POINT,
  PROMO_DURATION,
  IOS,
  ANDROID,
  PLARIUM_PLAY,
  PORTAL,
  GG_LINKS,
  TEMPLATE_ID,
  DEPENDENCIES,
  IS_RE_ENGAGEMENT,
  DYNAMIC_REWARD_PACKAGE,
  DYNAMIC_ADDITIONAL_REWARDS,
} from './constants';
import { influencerCampaignFormValidationSchema } from './schemas';

const includesPlatform = (platforms, platformId) =>
  platforms?.some(({ [PLATFORM_ID]: id }) => id === platformId);

const conversionPagesParams = ({
  [MASTER_CAMPAIGN]: masterCampaignId,
  [GG_LINKS]: { [PLATFORMS]: platforms },
}) => ({
  masterCampaignId,
  platforms: platforms.map(({ [PLATFORM_ID]: id }) => id),
});
const AgencySelect = withFormField()(({ area, ...props }) => {
  const setMultipleValues = useChangeMultipleValues(
    (
      fieldValue,
      {
        [DYNAMIC_ADDITIONAL_REWARDS]: dynamicAdditionalRewards,
        [DYNAMIC_REWARD_PACKAGE]: dynamicRewardPackage,
        [ADDITIONAL_REWARDS]: additionalRewards,
        [REWARD_PACKAGE]: rewardPackage,
        ...formValues
      }
    ) => {
      const opt = area.data.find(({ id }) => id === fieldValue);
      return {
        ...formValues,
        ...(opt?.isDynamicRewardSupported
          ? {
              [DYNAMIC_ADDITIONAL_REWARDS]: dynamicAdditionalRewards,
              [DYNAMIC_REWARD_PACKAGE]: dynamicRewardPackage,
            }
          : {
              [ADDITIONAL_REWARDS]: additionalRewards,
              [REWARD_PACKAGE]: rewardPackage,
            }),
        [props.name]: fieldValue,
      };
    }
  );
  return (
    <Select
      {...omitFormProps(props)}
      onChange={setMultipleValues}
      loading={area.loading}
      options={area.data}
    />
  );
});

const InfluencerCampaignForm = withForm({
  validationSchema: influencerCampaignFormValidationSchema,
  enableReinitialize: true,
})(({
  children,
  title,
  subTitle,
  values,
  setFieldValue,
  loading,
  onClose,
  isEdit,
  initialValid,
  onExited,
  open,
  entityId,
  hierarchyState,
}) => {
  const {
    [GENERATE_NAME_AUTOMATICALLY]: generateNameAutomatically,
    [INFLUENCER_CHANNEL]: influencerChannelValue,
    [AGENCY]: agencyValue,
    [MASTER_CAMPAIGN]: masterCampaignValue,
    [GG_LINKS]: ggLinks = {},
    [REWARD_PACKAGE]: rewardPackage,
    [DYNAMIC_REWARD_PACKAGE]: dynamicRewardPackage,
    [LANDING_PAGE]: landingPage,
    [IOS_LANDING_PAGE]: iOSLandingPage,
    [ANDROID_LANDING_PAGE]: androidLandingPage,
    [PROMO_CODE]: promoCode,
    [TEMPLATE_ID]: templateId,
  } = values;

  const { [PLATFORMS]: platforms } = ggLinks;

  const selectedPlariumPlayPlatform = includesPlatform(platforms, PLARIUM_PLAY);
  const selectedIOSPlatform = includesPlatform(platforms, IOS);
  const selectedAndroidPlatform = includesPlatform(platforms, ANDROID);
  const selectedPortalPlatform = includesPlatform(platforms, PORTAL);
  const [withTemplateId, setWithTemplateId] = useMountedState(false);

  useEffect(() => {
    if (
      !(selectedPlariumPlayPlatform || selectedPortalPlatform) &&
      landingPage
    ) {
      setFieldValue(LANDING_PAGE, null);
    }
  }, [selectedPlariumPlayPlatform]);

  useEffect(() => {
    if (!selectedIOSPlatform && iOSLandingPage) {
      setFieldValue(IOS_LANDING_PAGE, null);
    }
  }, [selectedIOSPlatform]);

  useEffect(() => {
    if (!selectedAndroidPlatform && androidLandingPage) {
      setFieldValue(ANDROID_LANDING_PAGE, null);
    }
  }, [selectedAndroidPlatform]);

  const validationName = useCallback(
    (value) => {
      if (!generateNameAutomatically && !value) {
        return 'Name is required';
      }
    },
    [generateNameAutomatically]
  );

  const loadOnMasterCampaign = useCallback(
    async ({ id }) => {
      const {
        platforms,
        promoCode: promoCodeMasterCampaign,
        templateIdAvailable,
      } = await loadMasterCampaign({
        id,
      });

      setWithTemplateId(templateIdAvailable);

      if (!templateIdAvailable && templateId) {
        setFieldValue(TEMPLATE_ID, '');
      }

      if (platforms.length === 1) {
        setFieldValue(`${GG_LINKS}.${PLATFORMS}`, [
          ggCampaignLinkSchema.cast({
            [PLATFORM_ID]: platforms[0].id,
          }),
        ]);
      }

      if (promoCodeMasterCampaign && !promoCode) {
        setFieldValue(PROMO_CODE, promoCodeMasterCampaign.id);
      }
    },
    [setFieldValue, promoCode, initialValid]
  );

  const loadOnInfuencersChannel = useCallback(
    async ({ channelId, agencyId }) => {
      const { priceWithFee } = await loadPriceWithFee({
        channelId,
        agencyId,
      });

      setFieldValue(`${COST_MODEL}.${PAYOUT}`, priceWithFee);
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (masterCampaignValue) {
      loadOnMasterCampaign({ id: masterCampaignValue });
    }
  }, [masterCampaignValue]);

  useEffect(() => {
    if (!initialValid && influencerChannelValue && agencyValue) {
      loadOnInfuencersChannel({
        channelId: influencerChannelValue,
        agencyId: agencyValue,
      });
    }
  }, [initialValid, influencerChannelValue, agencyValue]);

  const { data: platformsAllList, loading: platformsAllListLoading } =
    useHierarchyArea({
      name: `${GG_LINKS}.${PLATFORMS}`,
      source: loadPlatforms,
      depends: DEPENDENCIES[PLATFORMS],
      normalizeValue: (ggLinkPlatforms, platformsAllList) => {
        let dirty = false;

        const newGGPlatforms = ggLinkPlatforms?.filter(
          ({ [PLATFORM_ID]: platformId }) => {
            const exist = platformsAllList?.some(({ id }) => platformId === id);
            if (!exist) {
              dirty = true;
            }
            return exist;
          }
        );

        return dirty ? newGGPlatforms : ggLinkPlatforms;
      },
    });

  const handleChangePlatforms = useCallback(
    (_e, value) => {
      setFieldValue(
        `${GG_LINKS}.${PLATFORMS}`,
        value.map(
          (id) =>
            platforms?.find(
              ({ [PLATFORM_ID]: platformId }) => platformId === id
            ) ||
            ggCampaignLinkSchema.cast({
              [PLATFORM_ID]: id,
            })
        )
      );
    },
    [setFieldValue, platforms]
  );

  const selectedAgencyOption = hierarchyState[AGENCY]?.data?.find(
    ({ id }) => id === agencyValue
  );

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <FormField.TextField
                name={NAME}
                label={'Name'}
                basis={0}
                padding={0}
                fullWidth
                disabled={generateNameAutomatically}
                validate={validationName}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Switch
                name={GENERATE_NAME_AUTOMATICALLY}
                label={'Generate name automatically'}
                basis={0}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Master Campaign'}
                padding={0}
                name={MASTER_CAMPAIGN}
                source={loadMasterCampaigns}
                lazy={true}
                disabled={isEdit}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <MultiChangeSelect
                label={'Platforms'}
                padding={0}
                name={`${GG_LINKS}.${PLATFORMS}`}
                options={platformsAllList}
                loading={platformsAllListLoading}
                onMultiChange={handleChangePlatforms}
                disabled={isEdit}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Brief'}
                padding={0}
                name={BRIEF}
                source={loadBriefs}
                lazy={true}
                depends={DEPENDENCIES[BRIEF]}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Country'}
                name={COUNTRY}
                source={loadCountries}
                lazy={true}
                disabled={isEdit}
                padding={0}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Owner'}
                name={OWNER}
                source={isEdit ? loadUsers : loadActiveUsers}
                lazy={true}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                name={PROMO_CODE}
                label={'Promo code'}
                source={loadPromoCodes}
                lazy={true}
                padding={0}
                depends={DEPENDENCIES[PROMO_CODE]}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField.Checkbox
                name={IS_RE_ENGAGEMENT}
                label={'Track RE'}
                basis={0}
                padding={0}
              />
            </Grid>
          </Grid>
        </Section>
        <Section title={'Conversion Page'}>
          <Grid container spacing={3}>
            <Grid item xs={withTemplateId ? 8 : 12}>
              <FormField.Select
                name={LANDING_PAGE}
                label={'Conversion page'}
                padding={0}
                fullWidth
                source={loadLandingPages}
                lazy={true}
                helper={'Change Master Campaign’s conv. page'}
                params={conversionPagesParams}
                disabled={
                  !selectedPlariumPlayPlatform && !selectedPortalPlatform
                }
                depends={DEPENDENCIES[LANDING_PAGE]}
              />
            </Grid>
            {withTemplateId ? (
              <Grid item xs={4}>
                <FormField.TextField
                  name={TEMPLATE_ID}
                  label={'Template Id'}
                  padding={0}
                  disabled={isEdit}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormField.Select
                name={IOS_LANDING_PAGE}
                label={'iOS Conversion page'}
                padding={0}
                source={loadIOSLandingPages}
                lazy={true}
                disabled={!selectedIOSPlatform}
                depends={DEPENDENCIES[IOS_LANDING_PAGE]}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField.Select
                name={ANDROID_LANDING_PAGE}
                label={'Android Conversion page'}
                padding={0}
                source={loadAndroidLandingPages}
                lazy={true}
                disabled={!selectedAndroidPlatform}
                depends={DEPENDENCIES[ANDROID_LANDING_PAGE]}
              />
            </Grid>
          </Grid>
        </Section>
        <Section title={'Influencer'}>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <AgencySelect
                label={'Agency'}
                padding={0}
                name={AGENCY}
                source={loadAgencies}
                lazy={true}
                depends={DEPENDENCIES[AGENCY]}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Influencer channel'}
                padding={0}
                name={INFLUENCER_CHANNEL}
                source={loadInfuencersChannels}
                lazy={true}
                depends={DEPENDENCIES[INFLUENCER_CHANNEL]}
                required={true}
              />
            </Grid>
          </Grid>
        </Section>
        {selectedAgencyOption?.isDynamicRewardSupported ? (
          <Section title={'Dynamic Reward'}>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <FormField.MultiSelect
                  label={'Dynamic reward package'}
                  padding={0}
                  name={DYNAMIC_REWARD_PACKAGE}
                  source={loadRewardPackages}
                  lazy={true}
                  disabled={masterCampaignValue === undefined}
                  depends={DEPENDENCIES[DYNAMIC_REWARD_PACKAGE]}
                />
              </Grid>
              <Grid item xs={8}>
                {dynamicRewardPackage?.length ? (
                  <AdditionalRewards
                    name={DYNAMIC_ADDITIONAL_REWARDS}
                    addRewardLabel={'Dynamic additional reward package'}
                    rewardPackageLabel={'Additional Dynamic Reward package'}
                    limit={50}
                  />
                ) : null}
              </Grid>
            </Grid>
          </Section>
        ) : (
          <Section title={'Reward'}>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Reward package'}
                  padding={0}
                  name={REWARD_PACKAGE}
                  source={loadRewardPackages}
                  lazy={true}
                  disabled={masterCampaignValue === undefined}
                  depends={DEPENDENCIES[REWARD_PACKAGE]}
                />
              </Grid>
              <Grid item xs={8}>
                {rewardPackage && (
                  <AdditionalRewards
                    name={ADDITIONAL_REWARDS}
                    uniqueConditionSelection={true}
                  />
                )}
              </Grid>
            </Grid>
          </Section>
        )}
        <CostModelGroup
          name={COST_MODEL}
          source={loadCostModels}
          active={masterCampaignValue !== undefined}
          isEdit={isEdit}
          depends={DEPENDENCIES[COST_MODEL]}
        />
        <Section title={'Promo'}>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <FormField.Select
                label={'Location'}
                padding={0}
                name={PROMO_LOCATION}
                source={loadLocations}
                lazy={true}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.TimeInput
                label={'Starting point'}
                name={PROMO_STARTING_POINT}
                padding={0}
                placeholder={'00:00:00'}
                format={'##:##:##'}
                mask={['H', 'H', 'M', 'M', 'S', 'S']}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.TimeInput
                label={'Duration'}
                name={PROMO_DURATION}
                padding={0}
                placeholder={'00:00'}
                format={'##:##'}
                mask={['M', 'M', 'S', 'S']}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField.TextField
                name={CREATIVE_URL}
                label={'Creative URL'}
                padding={0}
              />
            </Grid>
          </Grid>
        </Section>
        <FormFieldGroup name={GG_LINKS}>
          <FormFieldGroup name={HYBRID_LINK_PARAMETERS}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Section
                  title={'iOS Product Page Params'}
                  subTitle={'URL params for custom product page'}
                >
                  <HybridLinkParameters name={IOS_CPP} />
                </Section>
              </Grid>
              <Grid item xs={6}>
                <Section
                  title={'Android Custom Product Page'}
                  subTitle={'URL params for custom product page'}
                >
                  <HybridLinkParameters name={ANDROID_CPP} />
                </Section>
              </Grid>
            </Grid>
          </FormFieldGroup>
        </FormFieldGroup>
        <Section title={'GG Campaigns'}>
          <Alert severity={'info'}>
            The relevant GG campaigns will be created automatically
          </Alert>
        </Section>
        {!isEmpty(platforms) ? (
          <GGCampaignLinks
            name={GG_LINKS}
            platforms={platforms}
            platformsAllList={platformsAllList}
            isEdit={isEdit}
          />
        ) : null}
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default InfluencerCampaignForm;
